import i18nFactory, { i18nRaw as i18nRawFactory } from '@yandex-int/i18n';
import classNames from 'classnames';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { RestrictFrequencyGroups } from '../../typings/log';
import {
    ExpandableCell,
    ExpandableProps
} from '../ExpandableCell/ExpandableCell';

import './RestrictFrequencyCell.css';
import * as keyset from './RestrictFrequencyCell.i18n';

const i18n = i18nFactory(keyset);
const i18nRaw = i18nRawFactory(keyset);

export interface RestrictFrequencyProps extends ExpandableProps {
    diff: RestrictFrequencyGroups;
}

export class RestrictFrequencyCell extends ExpandableCell<
    RestrictFrequencyProps
> {
    private static getPeriod(value: number): string {
        return value ? String(value) : i18n('company-period-time');
    }

    private static getStyledText(type: string, text: string, key: string) {
        return (
            <span
                key={key}
                className={Bem.cls('restrict-frequency-cell', type)}
            >
                {text}
            </span>
        );
    }

    protected getContent() {
        const {
            diff: { before, after }
        } = this.props;
        let block: React.ReactNode;

        if (before.value === 0 && after.value !== 0) {
            block = this.getGroup('added');
        } else if (before.value !== 0 && after.value === 0) {
            block = this.getGroup('removed');
        } else {
            block = this.getGroupChanged();
        }

        return (
            <div
                className={Bem.cls('restrict-frequency-cell')}
                ref={ref => (this.cell = ref)}
            >
                {block}
            </div>
        );
    }

    private getGroup(mode: string): React.ReactNode {
        const {
            diff: { after, before },
            isExpanded,
            canExpand
        } = this.props;

        return (
            <>
                <div
                    key={'shows-setting'}
                    className={classNames(
                        Bem.cls('restrict-frequency-cell', 'row'),
                        !isExpanded && canExpand
                            ? Bem.cls('expandable-cell', 'short-row')
                            : ''
                    )}
                >
                    {RestrictFrequencyCell.getStyledText(
                        mode,
                        i18n('shows-setting', {
                            limit: mode === 'added' ? after.value : before.value
                        }),
                        `shows-${mode}`
                    )}
                </div>
                <div
                    key={'days-setting'}
                    className={classNames(
                        Bem.cls('restrict-frequency-cell', 'row'),
                        !isExpanded && canExpand
                            ? Bem.cls('expandable-cell', 'short-row')
                            : ''
                    )}
                >
                    {RestrictFrequencyCell.getStyledText(
                        mode,
                        i18n('days-setting', {
                            limit: RestrictFrequencyCell.getPeriod(
                                mode === 'added' ? after.reset : before.reset
                            )
                        }),
                        `days-${mode}`
                    )}
                </div>
            </>
        );
    }

    private getGroupChanged(): React.ReactNode {
        const {
            diff: { after, before },
            isExpanded,
            canExpand
        } = this.props;

        return (
            <>
                <div
                    key={'shows-setting'}
                    className={classNames(
                        Bem.cls('restrict-frequency-cell', 'row'),
                        !isExpanded && canExpand
                            ? Bem.cls('expandable-cell', 'short-row')
                            : ''
                    )}
                >
                    {before.value === after.value
                        ? i18n('shows-setting', {
                              limit: before.value
                          })
                        : i18nRaw('shows-setting-changed', {
                              oldLimit: RestrictFrequencyCell.getStyledText(
                                  'removed',
                                  `${before.value}`,
                                  'shows-removed'
                              ),
                              newLimit: RestrictFrequencyCell.getStyledText(
                                  'added',
                                  `${after.value}`,
                                  'shows-added'
                              )
                          })}
                </div>
                <div
                    key={'days-setting'}
                    className={classNames(
                        Bem.cls('restrict-frequency-cell', 'row'),
                        !isExpanded && canExpand
                            ? Bem.cls('expandable-cell', 'short-row')
                            : ''
                    )}
                >
                    {before.reset === after.reset
                        ? i18n('days-setting', {
                              limit: RestrictFrequencyCell.getPeriod(
                                  before.reset
                              )
                          })
                        : i18nRaw('days-setting-changed', {
                              oldLimit: RestrictFrequencyCell.getStyledText(
                                  'removed',
                                  `${RestrictFrequencyCell.getPeriod(
                                      before.reset
                                  )}`,
                                  'days-removed'
                              ),
                              newLimit: RestrictFrequencyCell.getStyledText(
                                  'added',
                                  `${RestrictFrequencyCell.getPeriod(
                                      after.reset
                                  )}`,
                                  'days-added'
                              )
                          })}
                </div>
            </>
        );
    }
}
